import React from 'react';
import { z } from 'zod';

import { QuestionResponse, TypedQuestion } from 'types/question-response.types';

import {
  QuestionResponseFormValue,
  schematizeQuestion,
  initializeQuestion,
  serializeResponse
} from './question';

export type QuestionnaireFormValues = Record<number, QuestionResponseFormValue>;

const schemaForQuestionnaire = (questions: TypedQuestion[]): z.ZodType<QuestionnaireFormValues> => {
  const schema = questions.reduce((acc, question) => {
    const questionSchema = schematizeQuestion(question);
    if (!questionSchema) return acc;
    return { ...acc, [question.id]: questionSchema };
  }, {});
  return z.object(schema);
};

const serializeQuestionnaireResponses = (
  formValues: QuestionnaireFormValues
): QuestionResponse[] => {
  return Object.entries(formValues).reduce((acc: QuestionResponse[], [key, response]) => {
    const questionId = parseInt(key, 10);
    const formatted = serializeResponse(questionId, response);
    return formatted ? [...acc, formatted] : acc;
  }, []);
};

export const initializeQuestionnaire = (
  questions: TypedQuestion[],
  responses: QuestionResponse[] = []
): QuestionnaireFormValues => {
  const responseMap: Record<number, QuestionResponse> = responses.reduce(
    (acc, response) => ({ ...acc, [response.id]: response }),
    {}
  );
  return questions.reduce((acc: QuestionnaireFormValues, question) => {
    const response = responseMap[question.id];
    const initialValue = initializeQuestion(question, response);
    return initialValue ? { ...acc, [question.id]: initialValue } : acc;
  }, {});
};

interface UseQuestionnaireSchemaParams {
  hasQuestionnaire: boolean;
  questions: TypedQuestion[];
}
interface UseQuestionnaireSchemaReturn {
  questionnaireSchema: z.ZodType<QuestionnaireFormValues>;
  parseQuestionnaireFormValues: (formValues: QuestionnaireFormValues) => QuestionResponse[];
}
export const useQuestionnaireSchema = ({
  hasQuestionnaire,
  questions
}: UseQuestionnaireSchemaParams): UseQuestionnaireSchemaReturn => {
  const questionnaireSchema: z.ZodType<QuestionnaireFormValues> = React.useMemo(
    () => schemaForQuestionnaire(hasQuestionnaire ? questions : []),
    [hasQuestionnaire, questions]
  );
  const parseQuestionnaireFormValues = React.useCallback(
    (formValues: QuestionnaireFormValues): QuestionResponse[] => {
      const parsed = questionnaireSchema.safeParse(formValues);
      return parsed.success ? serializeQuestionnaireResponses(parsed.data) : [];
    },
    [questionnaireSchema]
  );
  return {
    questionnaireSchema,
    parseQuestionnaireFormValues
  };
};
